import { observer, use$ } from '@legendapp/state/react';
import { SAMPLE, C, satMeta, momMeta } from '../data/sample';
import type { Tab } from '../state';
import { state$, openNiche } from '../state';
import { NicheOverview } from './NicheOverview';
import { FunctionMatrix } from './FunctionMatrix';
import { Opportunities } from './Opportunities';
import { AppsList } from './AppsList';

const INGEST_ROWS = ['90%', '70%', '80%'];

export const NicheDetail = observer(function NicheDetail() {
  const nicheId = use$(state$.nicheId);
  const tab = use$(state$.tab);
  const d = SAMPLE;
  const niche = d.niches.find((n) => n.id === nicheId);
  if (!niche) return null;

  const sm = satMeta(niche.sat);
  const mm = momMeta(niche.mom);
  const ready = !!niche.ready;
  const oppCount = d.functions.filter((f) => f.opp.score >= 65).length;

  const tabDefs: { k: Tab; l: string; badge?: string }[] = [
    { k: 'overview', l: 'Overview & Saturation' },
    { k: 'matrix', l: 'Function Matrix', badge: 'HERO' },
    { k: 'opportunities', l: 'Opportunities', badge: '' + oppCount },
    { k: 'apps', l: 'Apps' },
  ];

  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', animation: 'nl-fade .3s ease' }}>
      {/* niche header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 13,
              background: niche.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
            }}
          >
            {niche.icon}
          </div>
          <div>
            <h1
              style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 5px' }}
            >
              {niche.name}
            </h1>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 13,
                color: '#6d7175',
                fontFamily: "'JetBrains Mono'",
              }}
            >
              <span>{niche.appsReal} apps</span>
              <span>·</span>
              <span>★ {niche.rating.toFixed(2)}</span>
              <span>·</span>
              <span style={{ color: mm.fg }}>
                {mm.arrow} {mm.label}
              </span>
            </div>
          </div>
        </div>
        {ready && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '13px 20px',
              background: '#fff',
              border: '1px solid #e1e3e5',
              borderRadius: 13,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: "'JetBrains Mono'",
                  fontSize: 30,
                  fontWeight: 700,
                  color: sm.fg,
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                }}
              >
                {niche.sat}
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  color: '#6d7175',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginTop: 3,
                }}
              >
                Saturation
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 12px',
                borderRadius: 8,
                background: sm.bg,
                fontSize: 13,
                fontWeight: 700,
                color: sm.fg,
              }}
            >
              {sm.label}
            </div>
          </div>
        )}
      </div>

      {/* tabs */}
      {ready && (
        <div
          style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e1e3e5', marginBottom: 22 }}
        >
          {tabDefs.map((t) => {
            const on = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => state$.assign({ tab: t.k, defId: null })}
                style={{
                  position: 'relative',
                  padding: '11px 16px',
                  background: 'none',
                  border: 'none',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: on ? 700 : 600,
                  color: on ? C.ink : C.ink3,
                  cursor: 'pointer',
                  borderBottom: `2px solid ${on ? C.primary : 'transparent'}`,
                  marginBottom: -1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                {t.l}
                {t.badge && (
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono'",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 20,
                      background: t.k === 'matrix' ? C.primary : '#fff1e6',
                      color: t.k === 'matrix' ? '#fff' : '#ea580c',
                    }}
                  >
                    {t.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ingest empty state for non-demo niches */}
      {!ready && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 16,
            padding: '54px 40px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: '#ebebeb',
              margin: '0 auto 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
            }}
          >
            ◷
          </div>
          <h2 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 8px' }}>
            This niche is awaiting analysis
          </h2>
          <p style={{ margin: '0 auto 22px', color: '#616161', fontSize: 14, maxWidth: 440 }}>
            ScanGap computes function matrices on demand. Request analysis to build the taxonomy +
            evidence for <b>{niche.name}</b>.
          </p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 9,
              maxWidth: 420,
              margin: '0 auto 24px',
            }}
          >
            {INGEST_ROWS.map((w, i) => (
              <div
                key={i}
                style={{
                  height: 14,
                  borderRadius: 6,
                  background: 'linear-gradient(90deg,#ebebeb 25%,#dadcde 50%,#ebebeb 75%)',
                  backgroundSize: '800px 100%',
                  animation: 'nl-shimmer 1.4s infinite',
                  width: w,
                }}
              />
            ))}
          </div>
          <button
            onClick={() => {
              state$.platformSlug.set('shopify');
              openNiche('reviews-ugc');
            }}
            style={{
              height: 42,
              padding: '0 22px',
              background: '#2c6ecb',
              border: 'none',
              borderRadius: 10,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            See a fully-analyzed niche →
          </button>
        </div>
      )}

      {ready && tab === 'overview' && <NicheOverview niche={niche} oppCount={oppCount} />}
      {ready && tab === 'matrix' && <FunctionMatrix />}
      {ready && tab === 'opportunities' && <Opportunities />}
      {ready && tab === 'apps' && <AppsList />}
    </div>
  );
});
